import { createContext, useCallback, useMemo, useState } from 'react';

export type ExtraCallFailedReason = 'ringtone-permission' | 'microphone-permission';

type CallFailed = {
  datetime: Date;
  reason: ExtraCallFailedReason;
};

type CallCapabilitiesContext = {
  hasFailedToCall: CallFailed | false;
  addFailedToCall: (reason: ExtraCallFailedReason) => void;
  dismissFailedToCall: () => void;
};

export const callCapabilitiesContext = createContext<CallCapabilitiesContext>({
  hasFailedToCall: false,
  addFailedToCall: () => {},
  dismissFailedToCall: () => {},
});

export const CallPlatformProvider = ({ children }: { children: React.ReactNode }) => {
  const [hasFailedToCall, setHasFailedToCall] = useState<CallFailed | false>(false);

  const addFailedToCall = useCallback((reason: ExtraCallFailedReason) => {
    setHasFailedToCall({
      datetime: new Date(),
      reason,
    });
  }, []);

  const dismissFailedToCall = useCallback(() => {
    setHasFailedToCall(false);
  }, []);

  const value = useMemo(() => {
    return {
      hasFailedToCall,
      addFailedToCall,
      dismissFailedToCall,
    } satisfies CallCapabilitiesContext;
  }, [hasFailedToCall, addFailedToCall, dismissFailedToCall]);

  return (
    <callCapabilitiesContext.Provider value={value}>{children}</callCapabilitiesContext.Provider>
  );
};
